/**
 * MessageBubble Component
 *
 * Individual message bubble with support for text, media, and tool results.
 * Displays differently based on message role (user/assistant/system).
 *
 * Usage:
 *   <MessageBubble
 *     role="assistant"
 *     content={{ text: "Hello!" }}
 *     showAvatar={true}
 *   />
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { MediaPreview } from './MediaPreview';
import { RichMessageText } from './RichMessageText';
import { CampaignProposalCard } from '@/components/chat/CampaignProposalCard';
import { PublishedCampaignStatus } from './PublishedCampaignStatus';
import { CampaignMarkdown } from './CampaignMarkdown';
import { LoadingIndicator } from './LoadingIndicator';
import { hasMediaTags } from '@/utils/mediaTagParser';
import type { PlatformAssignment } from '@/types/limits';

const paymentsEnabled = import.meta.env.VITE_PAYMENTS_ENABLED !== 'false';

export interface MessageBubbleProps {
  role: 'user' | 'assistant' | 'system';
  content: {
    text?: string;
    media?: Array<{ type: string; url: string; caption?: string; filename?: string }>;
    campaign_proposal?: any;
    published_campaign_results?: { assignments: PlatformAssignment[] };
    tool_results?: Array<{
      tool: string;
      status: 'success' | 'error';
      result?: any;
      error?: string;
    }>;
    workflow_status?: string;
    insufficient_credits?: boolean;
    type?: string;
  };
  // Pass conversation-level media for tag replacement
  conversationMedia?: Array<{ filename: string; url: string; type: string }>;
  showAvatar?: boolean;
  timestamp?: string;
  conversationId?: string;
  onPublish?: () => void;
  onToggleMediaCanvas?: () => void;
  isMediaCanvasOpen?: boolean;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  role,
  content,
  conversationMedia = [],
  showAvatar = true,
  timestamp,
  conversationId,
  onPublish,
  onToggleMediaCanvas,
  isMediaCanvasOpen,
}) => {
  const isUser = role === 'user';
  const isSystem = role === 'system';
  const isMediaWorkflowMessage =
    content.workflow_status === 'media_generation_pending' ||
    content.workflow_status === 'media_selection_pending';

  // Debug logging
  React.useEffect(() => {
    if (content.campaign_proposal) {
      console.log('[MessageBubble] Rendering with campaign_proposal:', {
        hasCampaignProposal: !!content.campaign_proposal,
        proposalType: typeof content.campaign_proposal,
        proposalKeys: Object.keys(content.campaign_proposal || {})
      });
    }
  }, [content.campaign_proposal]);

  // Format timestamp
  const formatTime = (isoString?: string) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // System messages (errors, notifications)
  if (isSystem) {
    // Stage progress indicator — neutral pill, not a warning
    if (content.type === 'stage_progress') {
      return (
        <div className="flex items-center justify-center gap-2 py-1">
          <div className="h-px flex-1 bg-inset dark:bg-border-dark" />
          <span className="text-xs text-ink-3 px-2 whitespace-nowrap">
            {content.text}
          </span>
          <div className="h-px flex-1 bg-inset dark:bg-border-dark" />
        </div>
      );
    }

    // Insufficient credits — show contextual message with CTA buttons
    if (content.insufficient_credits) {
      return (
        <div className="flex justify-center">
          <div className="max-w-2xl px-4 py-3 bg-warn-soft border border-warn/30 rounded-lg text-center">
            <p className="text-sm text-warn mb-3">
              {content.text || "You've run out of credits. Upgrade your plan or purchase more credits to continue generating campaigns."}
            </p>
            {paymentsEnabled && (
              <div className="flex justify-center gap-3 flex-wrap">
                <Link
                  to="/settings/billing"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors"
                >
                  Upgrade Plan
                </Link>
                <Link
                  to="/purchase-credits"
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-warn bg-warn-soft hover:bg-amber-200 border border-warn/40 rounded-lg transition-colors"
                >
                  Buy Credits
                </Link>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-center">
        <div className="max-w-2xl px-4 py-2 bg-warn-soft border border-warn/30 rounded-lg">
          <p className="text-sm text-warn text-center">
            {content.text}
          </p>
        </div>
      </div>
    );
  }

  // User and Assistant messages
  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar — the campaign page board shows plain bubbles, so avatars stay hidden */}
      {showAvatar && false && (
        <div className="flex-shrink-0">
          {isUser ? (
            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
              </svg>
            </div>
          )}
        </div>
      )}

      {/* Message Content */}
      <div className={`flex flex-col gap-2 max-w-2xl ${isUser ? 'items-end' : 'items-start'}`}>
        {/* Text Content — skipped when the card replaces it */}
        {content.text && !content.published_campaign_results && (
          <>
            {/* Check for loading indicator marker */}
            {content.text.startsWith('___LOADING_INDICATOR___') ? (
              <div className="bg-ground text-ink rounded-[18px] px-[14px] py-3">
                <LoadingIndicator message={content.text.replace('___LOADING_INDICATOR___', '')} />
              </div>
            ) : (
              <div
                className={`
                  rounded-[18px] px-[14px] py-3 text-[15px] leading-normal
                  ${isUser
                    ? 'bg-navy text-on-navy'
                    : 'bg-ground text-ink'
                  }
                `}
              >
                {/* For assistant messages, use markdown renderer with media tag support */}
                {!isUser ? (
                  hasMediaTags(content.text) ? (
                    <RichMessageText
                      text={content.text}
                      uploadedMedia={conversationMedia}
                      className="text-base leading-relaxed"
                    />
                  ) : (
                    <CampaignMarkdown content={content.text} />
                  )
                ) : (
                  <p className="text-base leading-relaxed whitespace-pre-wrap">
                    {content.text}
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* Media Workflow Toggle Button (After Phase 1, Before Phase 2) */}
        {isMediaWorkflowMessage && onToggleMediaCanvas && (
          <button
            onClick={onToggleMediaCanvas}
            className="flex items-center gap-2 px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors duration-200 border border-primary/30"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="text-sm font-medium">
              {isMediaCanvasOpen ? 'Hide Generated Media' : 'View Generated Media'}
            </span>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isMediaCanvasOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}

        {/* Media Content */}
        {content.media && content.media.length > 0 && (
          <MediaPreview media={content.media.map(m => ({
            type: m.type as 'image' | 'video' | 'audio',
            url: m.url,
            caption: m.caption
          }))} />
        )}

        {/* Campaign Proposal */}
        {content.campaign_proposal && (
          <CampaignProposalCard
            proposal={content.campaign_proposal}
            conversationMedia={conversationMedia}
            resolvedMedia={content.campaign_proposal?.resolved_media}
            conversationId={conversationId}
            onPublish={onPublish}
          />
        )}

        {/* Published Campaign Status Card (replaces plain-text publish summary) */}
        {content.published_campaign_results && conversationId && (
          <PublishedCampaignStatus
            conversationId={conversationId}
            initialAssignments={content.published_campaign_results.assignments}
          />
        )}

        {/* Tool Results */}
        {content.tool_results && content.tool_results.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {content.tool_results.map((result, idx) => (
              <div
                key={idx}
                className={`
                  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                  ${result.status === 'success'
                    ? 'bg-ok-soft text-ok'
                    : 'bg-bad-soft text-bad'
                  }
                `}
              >
                {result.status === 'success' ? (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span>{result.tool}</span>
              </div>
            ))}
          </div>
        )}

        {/* Timestamp */}
        {timestamp && showAvatar && (
          <p className="text-xs text-ink-3 px-2">
            {formatTime(timestamp)}
          </p>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
